import { FOODS } from "game/goods";
import { hashUnit } from "game/math";
import * as time from "game/time";
import { TRADE_LABELS } from "game/villages";
import { firstFreeJob } from "game/manager";

const always = () => true;

export const person = () => [
  [world=>world.festivalPlace, place=>[["walk_to",place],["festival_wait"]]],
  [world => world.worldArrival, entry => [["walk_to", entry?.tileId], ["end_world_arrival"]]],
  [world => world.worldJourney, trip => [["set_label"], ["walk_to", trip?.tileId], ["leave_edge", trip?.tileId, trip]]],
  [world => world.helpingFriend, () => [["end_friend_help"], ["person"]]],
  [
    world => world.capturedAttentionOf,
    targetId => [
      [ "release_attention", targetId ],
      [ "person" ]
    ]
  ],
  [
    world => world.currentJob,
    job => [
      [ "release_job", job ],
      [ "person" ]
    ]
  ],
  [
    world => world.claimedFarmable,
    farmableId => [
      [ "release_farmable", farmableId ],
      [ "person" ]
    ]
  ],
  [
    world => world.capturedBy,
    () => [
      [ "set_label", "waiting_for" ],
      [ "wait_for", time.HOUR * 0.5 ]
    ]
  ],
  [
    world => world.feeling.tired && (time.hour_of_day(world.days) >= 20 || time.hour_of_day(world.days) < 9),
    () => [
      [ "set_label" ],
      [ "move_to_place", "sleep", "allows_sleep" ],
      [ "go_to_sleep"]
    ],
  ],
  [
    world => world.feeling.hungry && time.hour_of_day(world.days) >= 6 && time.hour_of_day(world.days) < 21,
    () => [
      [ "set_label" ],
      [ "find_food" ]
    ]
  ],
  [world => world.chatVisit, () => [["chat_visit"]]],
  [world => world.customsVisit, () => [["customs_visit"]]],
  [
    always,
    () => [ [ "store_held" ] ]
  ],
  [
    world => !world.child,
    () => [
      [ "set_label" ],
      [ "check_jobs" ]
    ]
  ],
  [
    world => world.moveHome,
    house => [["walk_to", house], ["arrive_home"]]
  ],
  [
    world => world.social,
    () => [["social_visit"]]
  ],
  [world => world.friendOffer, offer => [["begin_friend_help", offer], ["walk_to", offer?.x != null ? { x: offer.x, y: offer.y } : offer?.targetId], ["help_friend_work"], ["end_friend_help"]]],
  [world => world.gardenHelp, garden => [["walk_to", garden], ["tend_garden", garden]]],
  [world => world.child && world.nightHome, home => [["home_downtime", home]]],
  [world => world.child, () => [["child_idle"]]],
  [
    world => time.hour_of_day(world.days) >= 6 && time.hour_of_day(world.days) < 18,
    () => [
      [ "set_label" ],
      [ "farm" ]
    ]
  ],
  [
    always,
    () => [["work_trade"]]
  ],
  [
    always,
    () => [ [ "haul", "wood" ] ]
  ],
  [world => world.nightHome, home => [["home_downtime", home]]],
  [always, () => [["idle_person"]]]
];

export const check_jobs = () => [
  [
    (_, jobs) => firstFreeJob(jobs, "build_shelter"),
    entry => [["take_job", entry.job.key], ["build_shelter", entry.targetId]]
  ],
  [
    (_, jobs) => firstFreeJob(jobs, "forage"),
    entry => entry.gatheredDone ? [["take_job", entry.job.key], ["carry_home"], ["complete_job", entry.job.key]] : [
      ["take_job", entry.job.key],
      ["walk_to", entry.targetId],
      ["set_label", "foraging"],
      ["forage", entry.targetId],
      ["carry_home"],
      ["complete_job", entry.job.key]
    ]
  ],
  [
    (_, jobs) => firstFreeJob(jobs, "move_to_here"),
    entry => [
      ["take_job", entry.job.key],
      ["walk_to", entry.targetId],
      ["complete_job", entry.job.key]
    ]
  ],
  [
    (_, jobs) => firstFreeJob(jobs, "create_field"),
    entry => [
      ["take_job", entry.job.key],
      ["set_label", "create_field"],
      ["walk_to", entry.targetId, { plotAccess: true }],
      ["create_field", entry.targetId ],
      ["complete_job", entry.job.key]
    ]
  ],
  [
    (_, jobs) => firstFreeJob(jobs, "create_stockpile"),
    entry => [
      ["take_job", entry.job.key],
      ["set_label", "create_stockpile"],
      ["walk_to", entry.targetId],
      ["create_stockpile", entry.targetId],
      ["complete_job", entry.job.key]
    ]
  ],
  [
    (_, jobs) => firstFreeJob(jobs, "cut_tree_down"),
    entry => entry.gatheredDone ? [["take_job", entry.job.key], ["carry_home"], ["complete_job", entry.job.key]] : [
      ["take_job", entry.job.key],
      ["set_label", "cut_tree_down"],
      ["walk_to", entry.targetId],
      ["chop_tree", entry.targetId],
      ["carry_home"],
      ["complete_job", entry.job.key]
    ]
  ]
];

export const build_shelter = targetId => [
  [
    world => !world.shelter?.needsLogs,
    () => [["walk_to", targetId], ["work_shelter", targetId], ["complete_job", "build_shelter"]]
  ],
  [
    world => world.holding.wood || world.holding.planks,
    () => [["walk_to", targetId], ["deliver_log", targetId], ["release_job", "build_shelter"]]
  ],
  [
    world => world.shelter?.source,
    source => [["walk_to", source], ["collect_log", source], ["walk_to", targetId],
      ["deliver_log", targetId], ["release_job", "build_shelter"]]
  ]
];

export const farm = () => [
  [
    always,
    () => [ [ "haul", "grain" ] ]
  ],
  [
    always,
    () => [ [ "harvest_any_field" ] ]
  ],
  [
    always,
    () => [ [ "sow_any_field" ] ]
  ],
  [
    always,
    () => [ [ "plough_any_field" ] ]
  ],
];

export const store_held = () => [
  [
    world => Object.keys(world.holding).filter(h => world.holding[h]),
    held => [
      [ "move_to_place", "container_with_space", "container_with_space", held ],
      [ "drop_entity_into_container", "container_with_space", held ],
      [ "forget_place", "container_with_space" ]
    ]
  ],
  [
    world => Object.keys(world.holding).filter(h => world.holding[h]),
    held => [
      [ "move_to_place", "stockpile_open_slot", "stockpile_open_slot", held ],
      [ "drop_entity_into_stockpile_slot", "stockpile_open_slot", held ],
      [ "forget_place", "stockpile_open_slot" ]
    ]
  ]
];

export const plough_any_field = () => [
  [
    world => world.places.ploughable_field,
    field => [
      [ "forget_place", "ploughable_field" ],
      [ "claim_farmable", field ],
      [ "walk_to", field ],
      [ "set_label", "plough_field" ],
      [ "plough_field", field ],
      [ "release_farmable", field ],
    ]
  ],
  [
    world => !world.places.ploughable_field,
    () => [
      [ "find_place", "ploughable_field", "farmable_slot_with", ["empty", "harvested", "rotten"] ],
    ]
  ]
];

export const harvest_any_field = () => [
  [
    world => world.places.harvestable_field,
    field => [
      [ "forget_place", "harvestable_field" ],
      [ "claim_farmable", field ],
      [ "walk_to", field ],
      [ "set_label", "harvest_field" ],
      [ "harvest_field", field ],
      [ "release_farmable", field ],
    ]
  ],
  [
    world => !world.places.harvestable_field,
    () => [
      [ "find_place", "harvestable_field", "farmable_slot_with", ["harvestable"] ],
    ]
  ]
];

export const sow_any_field = () => [
  [
    world => world.places.sowable_field,
    field => [
      [ "forget_place", "sowable_field" ],
      [ "claim_farmable", field ],
      [ "walk_to", field ],
      [ "set_label", "sow_field" ],
      [ "sow_field", field, "grain" ],
      [ "release_farmable", field ],
    ]
  ],
  [
    world => !world.places.sowable_field,
    () => [
      [ "find_place", "sowable_field", "farmable_slot_with", ["ploughed"] ],
    ]
  ]
];

export const haul = (thing) => [
  [
    always,
    () => [
      [ "find_place", "container_with_space", "container_with_space", thing ],
      [ "find_place", "haulable_with " + thing, "haulable_with_good", thing ],
      [ "set_label" ],
      [ "walk_to", "haulable_with " + thing ],
      [ "pick_up_entity_with_good", "haulable_with " + thing, thing ],
      [ "forget_place", "haulable_with " + thing ],
      [ "store_held" ],
    ]
  ],
  [
    always,
    () => [
      [ "find_place", "stockpile_open_slot", "stockpile_open_slot", thing ],
      [ "find_place", "haulable_with " + thing, "haulable_with_good", thing ],
      [ "set_label" ],
      [ "walk_to", "haulable_with " + thing ],
      [ "pick_up_entity_with_good", "haulable_with " + thing, thing ],
      [ "forget_place", "haulable_with " + thing ],
      [ "store_held" ],
    ]
  ]
];

export const go_to_sleep = () => [
  [
    always,
    () => [
      ["set_label", "sleeping"],
      ["sleep"]
    ]
  ]
];

export const move_to_place = (type, filter, param) => [
  [
    world => world.places[type],
    () => [
      ["walk_to", type]
    ]
  ],
  [
    always,
    () => [
      [ "find_place", type, filter, param ],
      [ "move_to_place", type, filter, param ]
    ],
  ]
];


export const find_food = () => [
  ...FOODS.map(good => [
    world => world.holding[good],
    () => [
      [ "set_label" ],
      [ "set_label", "eating" ],
      [ "eat", good ]
    ]
  ]),
  ...FOODS.map(good => [
    world => !world.holding[good],
    () => [ [ "make_meal_space" ], [ "pick_up", good, true ], [ "find_food" ] ]
  ]),
  [
    always,
    () => [
      [ "make_meal_space" ],
      [ "move_to_place", "forage_plant", "forage_plant" ],
      [ "set_label", "foraging" ],
      [ "forage", "forage_plant" ],
      [ "forget_place", "forage_plant" ],
      [ "find_food" ]
    ]
  ]
];

// TODO: Not yet complete. A number of these tasks don't yet exist: not quite
// ready to do crafting.
export const make_gruel = () => [
  [
    world => world.places.workshop && world.places.workshop.amounts.grain >= 5,
    () => [
      [ "move_to_place", "workshop", "workshop_with", "stove", { grain: 5 } ],
      [ "craft", "gruel" ],
    ]
  ],
  [
    world => !world.holding.grain,
    () => [
      [ "pick_up", "grain" ],
      [ "make_gruel" ],
    ]
  ],
  [
    world => world.holding.grain,
    () => [
      [ "move_to_place", "workshop", "workshop_with", "stove" ],
      [ "drop_entity_into_workshop", "workshop", "grain" ],
      [ "make_gruel" ]
    ]
  ],
];

export const pick_up = (good, storedFirst = false) => {
  const methods = [
    [
      always,
      () => [
        [ "move_to_place", "haulable_with " + good, "haulable_with_good", good ],
        [ "pick_up_entity_with_good", "haulable_with " + good, good ],
        [ "forget_place", "haulable_with " + good ],
      ]
    ],
    [
      always,
      () => [
        [ "move_to_place", "stockpile_with " + good, "stockpile_slot_with", good ],
        [ "pick_up_from_stockpile", "stockpile_with " + good, good ],
        [ "forget_place", "stockpile_with " + good ],
      ]
    ],
    [
      always,
      () => [
        [ "move_to_place", "container_with " + good, "container_with", good ],
        [ "pick_up_from_container", "container_with " + good, good ],
        [ "forget_place", "container_with " + good ],
      ]
    ]
  ];
  // Meals use the household reserve before competing for loose harvest.
  // Farming and hauling retain their existing resource order.
  return storedFirst ? [methods[2], methods[1], methods[0]] : methods;
};

export const plough_field = (targetId) => [
  [
    world => !world.subtasks || world.subtasks.some(t => t.id != targetId || t.result != "ploughed"),
    () => [
      [ "create_subtasks", targetId, "ploughed", ["empty", "harvested"] ],
      [ "plough_field", targetId ]
    ]
  ],
  [
    world => world.subtasks && world.subtasks.length == 0,
    () => [
      [ "clear_subtasks" ],
    ]
  ],
  [
    always,
    () => [
      [ "find_next_subtask", "subtask_slot" ],
      [ "walk_to", "subtask_slot" ],
      [ "perform_subtask_in_slot", targetId, "subtask_slot" ],
      [ "complete_subtask", "subtask_slot" ],
      [ "plough_field", targetId ]
    ]
  ]
];

export const harvest_field = (targetId) => [
  [
    world => !world.subtasks || world.subtasks.some(t => t.id != targetId || t.result != "harvested"),
    () => [
      [ "create_subtasks", targetId, "harvested", ["harvestable"] ],
      [ "harvest_field", targetId ]
    ]
  ],
  [
    world => world.subtasks && world.subtasks.length == 0,
    () => [
      [ "clear_subtasks" ],
    ]
  ],
  [
    always,
    () => [
      [ "find_next_subtask", "subtask_slot" ],
      [ "walk_to", "subtask_slot" ],
      [ "perform_subtask_in_slot", targetId, "subtask_slot" ],
      [ "complete_subtask", "subtask_slot" ],
      [ "harvest_field", targetId ]
    ]
  ]
];

export const sow_field = (targetId, seed) => [
  [
    world => !world.subtasks || world.subtasks.some(t => t.id != targetId || t.result != "sown"),
    () => [
      [ "create_subtasks", targetId, "sown", ["ploughed"], seed ],
      [ "sow_field", targetId, seed ]
    ]
  ],
  [
    world => world.subtasks && world.subtasks.length == 0,
    () => [
      [ "clear_subtasks" ],
    ]
  ],
  [
    world => !world.holding[seed],
    () => [
      [ "pick_up", seed ],
      [ "sow_field", targetId, seed ]
    ]
  ],
  [
    world => world.holding[seed],
    () => [
      [ "find_next_subtask", "subtask_slot" ],
      [ "walk_to", "subtask_slot" ],
      [ "perform_subtask_in_slot", targetId, "subtask_slot", seed ],
      [ "complete_subtask", "subtask_slot" ],
      [ "sow_field", targetId, seed ]
    ]
  ]
];

export const work_trade = () => [
  [world => world.tradeReady === false && world.profession, job => [["household_fuel",job.workplaceId]]],
  [
    world => world.tradeReady !== false && world.profession && time.hour_of_day(world.days) >= 8 && time.hour_of_day(world.days) < 17 && world.profession,
    job => [["walk_to", job.workplaceId], ["set_label", TRADE_LABELS[job.trade]], ["trade_work", job.workplaceId]]
  ]
];

export const social_visit = () => [
  [world => world.social?.kind == "secret" && world.social, visit => [
    ...(visit.stage == "returning" ? [] : [["walk_to", "socialDestination"], ["socialise", visit.partnerId], ["return_from_visit"]]),
    ["walk_to", visit.homeId], ["end_visit"], ["go_to_sleep"]
  ]],
  [world => world.social?.kind == "play" && world.social, visit => [["walk_to", "socialDestination"], ["play_together", visit.partnerId], ["end_visit"]]],
  [world => world.social?.stage == "returning" && world.social, visit => [["walk_to", visit.homeId], ["end_visit"]]],
  [world => world.social?.visitor && world.social, visit => [["walk_to", "socialDestination"], ["socialise", visit.partnerId], ["return_from_visit"], ["walk_to", visit.homeId], ["end_visit"]]],
  [world => world.social, visit => [["walk_to", "socialDestination"], ["socialise", visit.partnerId], ["end_visit"]]]
];

export const child_idle = () => [[always, () => [["wait_for", time.HOUR / 4]]]];
export const idle_person = () => [[
  world => 0.5 + hashUnit(world.actorId + ":idle:" + world.days) * 0.5,
  delay => [["set_label"], ["move_to_place", "meet", "space"], ["forget_place", "meet"], ["wait_for", time.HOUR * delay]]
]];
